import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios';
import _ from 'lodash';
import tokenSource from 'utils/AuthInfoSource';
import i18n from 'i18n';
import appConfig from 'config';
import { getServerErrorMessageConfig } from 'core/ServerErrorMessages';
import { ServerError } from 'core/ServerError';

let source = axios.CancelToken.source();

export function injectToken(
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig {
  if (!config.headers) {
    config.cancelToken = source.token;
    return config;
  }
  const token: string = tokenSource.getToken();
  if (token) {
    config.headers['x-auth-token'] = token;
  }
  config.headers['accept-language'] = i18n.language
    .toLowerCase()
    .replace('-', '_');
  if (appConfig.version) {
    // Tag every request with the running web app version for server-side tracking.
    config.headers['X-Version'] = appConfig.version;
  }
  config.cancelToken = source.token;
  return config;
}

export function updateToken(response: AxiosResponse<any>): AxiosResponse<any> {
  const token: string = response.headers['x-auth-token'];
  if (token) {
    const expiresIndex = 1;
    const parts = token.split(':');
    const expires = parts[expiresIndex];
    if (
      Number(expires) < new Date().getTime() &&
      tokenSource.invalidTokenHandler
    ) {
      tokenSource.invalidTokenHandler.tokenInvalidated();
      source.cancel();
      source = axios.CancelToken.source();
    } else {
      tokenSource.setToken(token, expires);
    }
  }
  return response;
}

function logout() {
  if (tokenSource.invalidTokenHandler) {
    tokenSource.invalidTokenHandler.tokenInvalidated();
    source.cancel();
    source = axios.CancelToken.source();
  }
}

// 後端 ServiceResInfo.ERR_AGENCY_ARCHIVED：當前身份的經銷商已封存時，
// 任何 API 回 HTTP 403，且 RestResponse body 的 status（業務錯誤碼）為 403030。
const ERR_AGENCY_ARCHIVED_CODE = 403030;
// 訊息 fallback（拿不到業務碼時才用）：後端預設英文，i18n 後為繁中訊息
const ERR_AGENCY_ARCHIVED_MESSAGES = [
  'Agency is archived',
  '此經銷商已封存，無法使用'
];
// 顯示說明 toast 後，延遲登出的秒數
const AGENCY_ARCHIVED_LOGOUT_DELAY_MS = 3000;
// 避免頁面同時多支 API 都回 403030 時重複跳 toast / 重複排程登出
let agencyArchivedLogoutScheduled = false;

// 只認「經銷商已封存」這個特定 403：優先用業務錯誤碼（data.status）判斷，
// 拿不到 code 時才退而比對訊息。其他一般 403 一律回 false（不會誤登出）。
function isAgencyArchivedError(businessCode: any, message: any): boolean {
  if (businessCode != null) {
    return businessCode === ERR_AGENCY_ARCHIVED_CODE;
  }
  return (
    typeof message === 'string' &&
    ERR_AGENCY_ARCHIVED_MESSAGES.includes(message)
  );
}

// 經銷商已封存：先用既有 toast 機制說明，3 秒後再登出。
// 不立即 logout()，避免當下清掉身份後停在「無權限」死路頁；期間身份仍有效，
// 使用者停在原頁看到說明，時間到才導回登入頁。
function handleAgencyArchived() {
  if (agencyArchivedLogoutScheduled) {
    return;
  }
  agencyArchivedLogoutScheduled = true;
  window.dispatchEvent(
    new CustomEvent<any>('show-error-toast', {
      detail: {
        messageToShow: i18n.t('serverResponse.403030')
      }
    })
  );
  setTimeout(() => {
    agencyArchivedLogoutScheduled = false;
    logout();
  }, AGENCY_ARCHIVED_LOGOUT_DELAY_MS);
}

export function autoLogout(error: any): any {
  const code = _.get(error, 'response.status');
  if (code === 401) {
    logout();
  }
  return Promise.reject(error);
}

export function errorHandler(error: AxiosError): any {
  const apiError: ErrorEventInit = { error };
  const apiErrorEvent = new ErrorEvent('api-error', apiError);
  window.dispatchEvent(apiErrorEvent);
  const status = _.get(error.response, 'data.status');
  const message = _.get(error.response, 'data.message');
  const detailObj = _.get(error.response, 'data.detailObj');
  const code = _.get(error, 'response.status');

  // 經銷商已封存：收到此特定 403（業務碼 403030）跳說明 toast、3 秒後登出。
  // 放在其他 403 處理之前短路，避免落到一般 403 的 show-error-page 死路頁。
  // 回 reject(null) 表示已處理，避免呼叫端再自行處理／再導頁。
  if (code === 403 && isAgencyArchivedError(status, message)) {
    handleAgencyArchived();
    return Promise.reject(null);
  }

  if (status && !(code === 403 || code === 404)) {
    const errorMessageConfig = getServerErrorMessageConfig(
      status,
      message ? message : i18n.t('serverResponse.500000')
    );
    const messageToShow = errorMessageConfig.message;
    let errorUserTitle = null;
    let errorUserMsg = null;

    if (detailObj) {
      errorUserTitle = _.get(detailObj, 'error.error_user_title');
      errorUserMsg = _.get(detailObj, 'error.error_user_msg');
    }
    const isInvalidTokenError = status === 404012;
    errorMessageConfig.showAlert
      ? window.dispatchEvent(
          new CustomEvent<any>('show-error-modal', {
            detail: {
              errorMessageConfig: {
                ...errorMessageConfig,
                callback: isInvalidTokenError ? logout : null
              }
            }
          })
        )
      : window.dispatchEvent(
          new CustomEvent<any>('show-error-toast', {
            detail: {
              messageToShow,
              errorUserTitle,
              errorUserMsg
            }
          })
        );
    return Promise.reject(null);
  } else {
    if (code) {
      window.dispatchEvent(
        new CustomEvent<any>('show-error-page', { detail: { code } })
      );
      const errorMessage = getServerErrorMessageConfig(+`${code}000`);
      return Promise.reject(
        new ServerError(code, errorMessage.message, message)
      );
    }
    return Promise.reject(error);
  }
}

export function cancelRequest(message = '') {
  source.cancel(message);
  source = axios.CancelToken.source();
}

const client = axios.create({
  baseURL: appConfig.baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

client.interceptors.request.use(injectToken);
client.interceptors.response.use(updateToken, autoLogout);
client.interceptors.response.use((res) => res, errorHandler);

export const clientWithoutErrorHandler = axios.create({
  baseURL: appConfig.baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});
clientWithoutErrorHandler.interceptors.request.use(injectToken);
clientWithoutErrorHandler.interceptors.response.use(updateToken, autoLogout);

export default client;
